import { DECOR_LIBRARY_CATEGORIES, DECOR_LIBRARY_CATEGORY_SPECS, DRESSES_TABLES, } from '../types/decor-library.js';
import { hexToHsl, readDecorLook } from './decor.js';
/** Whether a value names a library category. */
export function isDecorLibraryCategory(value) {
    return (typeof value === 'string' && DECOR_LIBRARY_CATEGORIES.includes(value));
}
/** A look narrowed to what a category decides — a chair never sets the linen. */
export function categoryLook(category, look) {
    const clean = readDecorLook(look, false);
    const out = {};
    DECOR_LIBRARY_CATEGORY_SPECS[category].fields.forEach((f) => {
        if (clean[f] !== undefined)
            out[f] = clean[f];
    });
    return out;
}
/** The categories that dress an object, in the library's order. */
export function categoriesForObject(object) {
    return DECOR_LIBRARY_CATEGORIES.filter((c) => {
        const dresses = DECOR_LIBRARY_CATEGORY_SPECS[c].dresses;
        if (dresses.includes(object.objectType))
            return true;
        // A cocktail table is a table, but it is not laid for dinner.
        if (object.objectType === 'COCKTAIL_TABLE')
            return false;
        return object.isTable && dresses.includes(DRESSES_TABLES);
    });
}
/** A library piece as a pick: its id, name and its category's fields. */
export function pickOf(item) {
    return { itemId: item.id, name: item.name, look: categoryLook(item.category, item.look) };
}
/** A look with a category's pick set (or taken away, with null). */
export function withPick(look, category, pick) {
    const picks = { ...(look.picks ?? {}) };
    if (pick)
        picks[category] = pick;
    else
        delete picks[category];
    const next = { ...look };
    if (Object.keys(picks).length)
        next.picks = picks;
    else
        delete next.picks;
    return next;
}
/**
 * Takes a category's fields off a look — choosing a library piece replaces
 * what was set by hand for the same things, so the pick is what shows.
 */
export function withoutCategoryFields(look, category) {
    const next = { ...look };
    DECOR_LIBRARY_CATEGORY_SPECS[category].fields.forEach((f) => delete next[f]);
    return next;
}
/** Which colour family a colour belongs to. Pure. */
export function colorFamily(hex) {
    const { h, s, l } = hexToHsl(hex);
    if (l >= 0.93)
        return 'white';
    if (l <= 0.12)
        return 'black';
    if (s < 0.12)
        return l >= 0.82 ? 'white' : l <= 0.25 ? 'black' : 'grey';
    // Warm, pale and barely coloured: ivory and champagne.
    if (l >= 0.78 && s < 0.5 && h >= 25 && h <= 70)
        return 'ivory';
    if (h >= 30 && h < 55 && s >= 0.25 && s < 0.75 && l >= 0.35 && l < 0.78)
        return 'gold';
    if (h >= 330 || h < 12)
        return l >= 0.7 ? 'pink' : 'red';
    if (h < 40)
        return l < 0.38 ? 'brown' : 'orange';
    if (h < 65)
        return 'yellow';
    if (h < 170)
        return 'green';
    if (h < 255)
        return 'blue';
    if (h < 300)
        return 'purple';
    return 'pink';
}
/** The colour families a piece shows: its own colours, then the look's. */
export function itemColorFamilies(item) {
    const look = item.look;
    const fromLook = Object.entries(look).flatMap(([k, v]) => k.endsWith('Color') && typeof v === 'string'
        ? [v]
        : k.endsWith('Colors') && Array.isArray(v)
            ? v
            : []);
    return new Set([...item.colors, ...fromLook].map(colorFamily));
}
/** Whether a piece passes a filter. Pure — the picker and the server ask the same. */
export function matchesDecorLibraryFilter(item, filter) {
    if (!filter.includeInactive && !item.isActive)
        return false;
    if (filter.category && item.category !== filter.category)
        return false;
    const words = (filter.q ?? '').toLowerCase().split(/\s+/).filter(Boolean);
    if (words.length) {
        const text = [
            item.name,
            item.description ?? '',
            ...item.tags,
            ...item.vendors.map((v) => v.name),
        ]
            .join(' ')
            .toLowerCase();
        if (!words.every((w) => text.includes(w)))
            return false;
    }
    if (filter.tags?.length) {
        const tags = new Set(item.tags.map((t) => t.toLowerCase()));
        if (!filter.tags.every((t) => tags.has(t.toLowerCase())))
            return false;
    }
    if (filter.vendorIds?.length) {
        if (!item.vendors.some((v) => filter.vendorIds.includes(v.vendorId)))
            return false;
    }
    if (filter.colors?.length) {
        const families = itemColorFamilies(item);
        if (!filter.colors.some((c) => families.has(c)))
            return false;
    }
    return true;
}
/** Tags written the one way: trimmed, lower case, de-duplicated, at most `max`. */
export function normalizeDecorTags(tags, max = 20) {
    if (!Array.isArray(tags))
        return [];
    const out = [];
    tags.forEach((t) => {
        if (typeof t !== 'string')
            return;
        const clean = t.trim().toLowerCase().replace(/\s+/g, ' ').slice(0, 40);
        if (clean && !out.includes(clean))
            out.push(clean);
    });
    return out.slice(0, max);
}
/** Every tag used across the pieces, most used first. */
export function libraryTags(items) {
    const counts = new Map();
    items.forEach((i) => i.tags.forEach((t) => counts.set(t, (counts.get(t) ?? 0) + 1)));
    return [...counts.entries()]
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .map(([t]) => t);
}
/**
 * The library pieces picked anywhere on a plan — the event's look, the
 * plan's and each object's own — first seen first, each with the category it
 * was picked for. What the AI render shows the model.
 */
export function pickedLibraryItems(looks) {
    const seen = new Map();
    looks.forEach((look) => Object.entries(look?.picks ?? {}).forEach(([category, pick]) => {
        if (pick && !seen.has(pick.itemId)) {
            seen.set(pick.itemId, { itemId: pick.itemId, name: pick.name, category });
        }
    }));
    return [...seen.values()];
}
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
/**
 * Applies to `target` only what changed between `before` and `after` — a
 * field set or cleared, a category's pick chosen or taken away. Used when one
 * choice is made for several objects whose own looks differ: each keeps what
 * the choice did not touch. Choosing a pick also clears that category's
 * hand-set fields on the target, as it does on one object. Pure.
 */
export function applyLookChange(before, after, target) {
    let out = { ...target };
    const b = before;
    const a = after;
    const fields = new Set([...Object.keys(b), ...Object.keys(a)].filter((k) => k !== 'picks'));
    fields.forEach((k) => {
        if (same(b[k], a[k]))
            return;
        const next = { ...out };
        if (a[k] === undefined)
            delete next[k];
        else
            next[k] = a[k];
        out = next;
    });
    const categories = new Set([
        ...Object.keys(before.picks ?? {}),
        ...Object.keys(after.picks ?? {}),
    ]);
    categories.forEach((c) => {
        const was = before.picks?.[c];
        const now = after.picks?.[c];
        if (same(was, now) || !isDecorLibraryCategory(c))
            return;
        out = now ? withPick(withoutCategoryFields(out, c), c, now) : withPick(out, c, null);
    });
    return out;
}
/** Whether a level sets every field a category decides, by hand — its picks below are not seen. */
function coversCategory(look, category) {
    const l = (look ?? {});
    return DECOR_LIBRARY_CATEGORY_SPECS[category].fields.every((f) => l[f] !== undefined);
}
/**
 * The library pieces a plan actually shows — what the AI render is shown.
 * A plan's pick replaces the event's for its category, and the event's is
 * dropped where the plan sets that category's every field by hand; a level's
 * pick counts only while some object it dresses does not choose its own
 * (a pick or every field). Each object's own picks count. First seen first.
 * Pure.
 */
export function piecesInUse(eventLook, planLook, objects) {
    const out = new Map();
    const add = (category, pick) => {
        if (pick && !out.has(pick.itemId)) {
            out.set(pick.itemId, { itemId: pick.itemId, name: pick.name, category });
        }
    };
    DECOR_LIBRARY_CATEGORIES.forEach((c) => {
        const levelPick = planLook?.picks?.[c] ?? (coversCategory(planLook, c) ? undefined : eventLook?.picks?.[c]);
        if (!levelPick)
            return;
        const dressed = objects.filter((o) => categoriesForObject(o).includes(c));
        const usedByOne = dressed.some((o) => !o.decor?.picks?.[c] && !coversCategory(o.decor, c));
        if (usedByOne)
            add(c, levelPick);
    });
    objects.forEach((o) => Object.entries(o.decor?.picks ?? {}).forEach(([c, pick]) => {
        if (isDecorLibraryCategory(c) && categoriesForObject(o).includes(c))
            add(c, pick);
    }));
    return [...out.values()];
}
//# sourceMappingURL=decorLibrary.js.map